import { useCallback } from 'react';
import { addBreadcrumb, captureException } from '@sentry/react';
import {
  FormType,
  PersonDetails,
  SubmissionMode,
  SubmitFormRequest,
  SubmitFormResponse,
} from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { FormsV1 } from '@frontend/api-digital-forms';
import { DEFAULT_DFX_HTTP_OPTIONS } from '../../../../../constants';
import { getISODate } from '../../../../../helpers/date-helpers';
import { useConfigShallowStore } from '../../../../../stores';
import { buildSubmitFormsPayload, DynamicFormLiveState } from '../submit-form.helpers';
import { SchemaForm } from '../types';

export interface UseSubmitDynamicFormArgs {
  schemaForms: SchemaForm[];
  locationId: string;
  type: FormType;
  mode: SubmissionMode;
  /** Form/packet id, for unsolicited (public/kiosk) submissions. */
  documentId?: string;
  /** Existing submission id, for solicited submissions. */
  submissionId?: string;
}

// Omit empty/undefined fields rather than sending them as empty strings on the wire.
const buildPersonDetails = (person?: PersonDetails): PersonDetails | undefined => {
  if (!person) return undefined;
  const normalized = person.birthdate ? { ...person, birthdate: getISODate(person.birthdate) } : person;
  const entries = Object.entries(normalized).filter(([, value]) => value !== undefined && value !== '');
  return entries.length ? (Object.fromEntries(entries) as PersonDetails) : undefined;
};

/** Wires the submit-form mutation to live store state, with Sentry breadcrumbs/error logging. */
export const useSubmitDynamicForm = ({
  schemaForms,
  locationId,
  type,
  mode,
  documentId,
  submissionId,
}: UseSubmitDynamicFormArgs) => {
  const { person } = useConfigShallowStore('person');
  const mutation = FormsV1.Mutations.useSubmitFormMutation();

  const submitForm = useCallback(
    async (liveState: DynamicFormLiveState): Promise<SubmitFormResponse> => {
      const payload = buildSubmitFormsPayload({ schemaForms, ...liveState });

      const request: SubmitFormRequest = {
        companyId: locationId,
        type,
        mode,
        documentId,
        submissionId,
        personDetails: buildPersonDetails(person),
        forms: payload.forms,
        attachmentDetails: payload.attachmentDetails,
      };

      addBreadcrumb({
        category: 'digital-forms.submit',
        message: 'Submitting form',
        level: 'info',
        data: { type, mode, documentId, submissionId, locationId, formCount: payload.forms.length },
      });

      try {
        const response = await mutation.mutateAsync({ ...request, _httpOptions: DEFAULT_DFX_HTTP_OPTIONS });

        addBreadcrumb({
          category: 'digital-forms.submit',
          message: 'Form submitted successfully',
          level: 'info',
          data: { submissionId: response.submissionId },
        });

        return response;
      } catch (error) {
        captureException(error, {
          tags: { feature: 'digital-forms-submission' },
          extra: { type, mode, documentId, submissionId, locationId },
        });
        throw error;
      }
    },
    [schemaForms, locationId, type, mode, documentId, submissionId, person, mutation]
  );

  return { submitForm, isSubmitting: mutation.isLoading, submissionResponse: mutation.data };
};
